/**
 * The last thing between a render error and a blank page.
 *
 * Without this, any error thrown while rendering unmounts the React root and
 * the member gets white. That is indistinguishable from "still loading", "not
 * signed in", "the server is down" and "there is a bug", which is why issue #36
 * calls it unusable for the member, the helpdesk and the developer at once.
 *
 * Mounted OUTSIDE the providers in index.tsx, deliberately: a provider that
 * fails on the way up would unmount a boundary inside it, by the very error it
 * exists to catch. That copy has no theme above it, so it brings its own: the
 * look this browser remembers, else Meridian, and Meridian also when building
 * the remembered look is what failed.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { BrandCard } from './brand/BrandCard';
import { StartingScreen } from './brand/StartingScreen';

import '../i18n';
import { browserStorage, LookContext, rememberedTheme } from '../contexts/look';
import { isConnectivityError } from '../monitoring/connectivity';
import { captureException } from '../monitoring/sentry';
import {
  browserRecoveryDeps,
  isModuleLoadError,
  recoverFromStaleBundle,
  type Recovery,
} from '../monitoring/staleBundle';
import { Box, Button, CssBaseline, Stack, Text, ThemeProvider } from '../ui';

interface FallbackProps {
  error: Error;
  onRetry: () => void;
  /**
   * When the caller knows better than the error's own shape. Chrome words a
   * missing chunk as "Failed to fetch…", which reads as offline to
   * isConnectivityError even when the platform answered.
   */
  offline?: boolean;
}

export const ErrorFallback: React.FC<FallbackProps> = ({
  error,
  onRetry,
  offline = isConnectivityError(error),
}) => {
  const { t } = useTranslation();

  return (
    <BrandCard role="alert">
      <Box>
        <Text level="h5" as="h1">
          {t(offline ? 'errorBoundary.offlineTitle' : 'errorBoundary.title')}
        </Text>
        <Text tone="text.secondary" styles={{ mt: 1 }}>
          {t(offline ? 'errorBoundary.offlineDescription' : 'errorBoundary.description')}
        </Text>
      </Box>

      <Stack spacing={1.25} styles={{ width: '100%' }}>
        <Button fullWidth size="lg" emphasis="solid" onClick={onRetry}>
          {t('errorBoundary.retry')}
        </Button>
        <Button fullWidth emphasis="outline" onClick={() => window.location.reload()}>
          {t('errorBoundary.reload')}
        </Button>
      </Stack>
    </BrandCard>
  );
};

const RememberedLook: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme] = React.useState(() => rememberedTheme(browserStorage()));
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

interface Props {
  children: React.ReactNode;
  /**
   * Which of the two mount points caught this, as a Sentry tag.
   *
   * `page` is the copy inside ThemeProvider and is what a member normally
   * sees; `root` is the copy outside every provider in index.tsx, and an error
   * reaching it means a provider itself failed — a different class of problem
   * and worth telling apart in an issue list. Sent as a Sentry tag, so it is
   * filterable. A name, not data.
   */
  boundary: 'root' | 'page';
}

interface State {
  error: Error | null;
  /**
   * Only for a page whose code could not be loaded; see staleBundle.ts. A
   * reload leaves it on `checking`, so the spinner stays until the page goes.
   */
  recovery: Exclude<Recovery, 'reloading'> | 'checking' | null;
}

class AppErrorBoundary extends React.Component<Props, State> {
  static contextType = LookContext;
  declare context: React.ContextType<typeof LookContext>;

  state: State = { error: null, recovery: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, recovery: isModuleLoadError(error) ? 'checking' : null };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (isModuleLoadError(error)) {
      void this.recover(error, info.componentStack);
      return;
    }
    this.report(error, info.componentStack);
  }

  private report(error: Error, componentStack?: string | null) {
    captureException(error, {
      // componentStack is React's own frame list, not request data.
      extra: { componentStack },
      // A tag, not extra: Sentry does not index extra, so an operator asking
      // "did a provider fail, or a page?" could not filter on it there.
      tags: { boundary: this.props.boundary },
    });
  }

  /** A stale bundle after a deploy is expected, so only a failed recovery is reported. */
  private async recover(error: Error, componentStack?: string | null) {
    const recovery = await recoverFromStaleBundle(browserRecoveryDeps());
    if (recovery === 'reloading') return;
    this.report(error, componentStack);
    this.setState({ recovery });
  }

  private retry = () => {
    const { error } = this.state;
    // React.lazy keeps the rejected import, so rendering again cannot succeed.
    if (error !== null && isModuleLoadError(error)) {
      this.setState({ recovery: 'checking' });
      void this.recover(error);
      return;
    }
    this.setState({ error: null, recovery: null });
  };

  render() {
    const { error, recovery } = this.state;
    if (error === null) return this.props.children;
    const screen =
      recovery === 'checking' ? (
        <StartingScreen />
      ) : (
        <ErrorFallback
          error={error}
          onRetry={this.retry}
          offline={recovery === null ? undefined : recovery === 'unreachable'}
        />
      );
    // No LookProvider above means no theme above either: this is the outer copy.
    return this.context === null ? <RememberedLook>{screen}</RememberedLook> : screen;
  }
}

export default AppErrorBoundary;
